import React from 'react';
import throttle from 'lodash/throttle';
import connectToStores from 'alt-utils/lib/connectToStores';
import './ImmediateCoachingContainer.styl';
import WavyCard from 'components/pure/utils/WavyCard';
import {CallToAction} from 'components/pure/tooltips/CallToAction';
import theme from 'styles/theme';
import classNames from 'classnames';
import AdHocCoachingDialogContainer from 'components/coaching/AdHocCoachingDialogContainer';
import {browserHistory, Link} from 'react-router';
import LiveCoachingActions from 'actions/scorecards/LiveCoachingActions';
import AuthStore from 'stores/AuthStore';
import CoachingPlan from 'records/CoachingPlan';
import {nonSkillRelated} from 'records/CoachingContentDetail';
import CoachingReviewTypeStore from 'stores/coaching/CoachingReviewTypeStore';
import moment from 'moment';
import CoachingPlanActions from 'actions/coaching/CoachingPlanActions';
import CoachingReviewActions from 'actions/coaching/CoachingReviewActions';
import Loading from 'components/pure/utils/Loading';
import CoachingReviewTypeActions from 'actions/coaching/CoachingReviewTypeActions';
import Button from '@material-ui/core/Button';
import {getMuiV4Theme} from 'utils/css/themeUtils';
import Dialog from '@material-ui/core/Dialog';
import DialogTitle from '@material-ui/core/DialogTitle';
import IconButton from '@material-ui/core/IconButton';
import {XMarkLargeIcon} from '@components/icon/XMarkLargeIcon';

const liveCoachingAdhocPlan = ({now, coach, rep, reviewTypeId}) =>
  new CoachingPlan({
    adHoc: false,
    livecoaching: true,
    contentDetails: [nonSkillRelated],
    schedule: {
      weeksBetween: 1,
      daysOfWeek: [now.format('dddd')],
      time: {hour: now.hours(), minute: now.minutes()},
      recurrenceCount: 1,
      durationInMinutes: 15,
    },
    coach: {
      id: coach.id,
      info: coach,
      prepNumberOfCalls: 0,
      prepShouldComment: false,
    },
    recipient: {
      id: rep.id,
      info: rep,
      prepNumberOfCalls: 0,
      prepShouldComment: false,
    },
    name: `Live Coaching Session, ${now.format('MMM DD YYYY')}`,
    objective: `${coach.firstName} provided live feedback to ${rep.firstName}`,
    reviewTypeIds: [reviewTypeId],
  });

@connectToStores
export default class ImmediateCoachingContainer extends React.Component {
  static getStores() {
    return [CoachingReviewTypeStore];
  }

  static getPropsFromStores() {
    return {
      types: CoachingReviewTypeStore.getState().types,
    };
  }

  constructor(props) {
    super(props);

    this.state = {
      settingUpAdhocMeeting: false,
      loading: false,
      rep: undefined,
      ready: false,
    };

    this.startLiveCoaching = throttle(this.startLiveCoaching.bind(this), 5000);
  }

  async componentDidMount() {
    await CoachingReviewTypeActions.fetchAllReviewTypes();
    this.setState({ready: true});
  }

  startLiveCoaching() {
    const {rep, types} = this.props;
    const coach = AuthStore.getState().user;
    const callFocusedType = types.find(t => t.includesCalls);

    this.setState({loading: true}, async () => {
      const adhocPlan = liveCoachingAdhocPlan({
        rep,
        coach,
        now: moment(),
        reviewTypeId: callFocusedType.id,
      });

      const planPromise = CoachingPlanActions.create(adhocPlan).then(async plan => {
        const retrievedPlan = await CoachingPlanActions.retrieve(plan.id, {
          includeReviews: true,
        });
        const review = retrievedPlan.getNextReview();

        return [plan.id, review];
      });

      const metadataPromise = LiveCoachingActions.createEmpty([rep.id, coach.id]);

      const [[planId, review], metadata] = await Promise.all([planPromise, metadataPromise]);

      CoachingReviewActions.startReview(review.id, true).then(() => {
        browserHistory.push({
          pathname: `/live-coaching`,
          state: {
            callId: metadata.id,
            callerId: rep.id,
            coachId: coach.id,
            planId: planId,
            back: window.location.pathname + window.location.search,
          },
        });
      });
    });
  }

  render() {
    const {onClose, rep, fileUploadId, types} = this.props;
    const {settingUpAdhocMeeting, loading, ready} = this.state;

    if (!ready) return null;

    // need to know if we launched primarily with a call, or just the rep
    const launchedWithCall = fileUploadId != null;

    const noCallNoRep = !fileUploadId && !rep;
    const showLiveCoaching = !settingUpAdhocMeeting && !launchedWithCall && !noCallNoRep;
    const hasNoCallReviewType = types.some(t => !t.includesCalls);

    const adhocSelection = (
      <div>
        <p>
          {launchedWithCall
            ? `Provide feedback to ${rep ? rep.firstName : 'the agent'} related to this call`
            : `Meet with ${
                rep ? rep.firstName : 'the agent'
              } and record details of the conversation in Coach.`}
        </p>
        <Button
          variant="contained"
          color="primary"
          fullWidth
          className={'create-button meeting'}
          onClick={() => this.setState({settingUpAdhocMeeting: true})}>
          Coach Now
        </Button>
      </div>
    );

    const setUpAdhoc = (
      <div>
        <AdHocCoachingDialogContainer
          {...this.props}
          rep={this.state.rep || this.props.rep}
          setRep={rep => this.setState({rep})}
          showRepSelect={!rep}
          callId={fileUploadId}
          breakOut
        />
      </div>
    );

    return (
      <Dialog className="immediate-coaching-dialog" open onRequestClose={onClose} actions={[]}>
        <DialogTitle disableTypography className="dialog-title" onClose={onClose}>
          {!settingUpAdhocMeeting || launchedWithCall ? (
            'Coach Now'
          ) : (
            <span
              className={classNames('generic-link', 'pointer')}
              onClick={() => this.setState({settingUpAdhocMeeting: false})}>
              &lt; BACK
            </span>
          )}
          <IconButton onClick={onClose}>
            <XMarkLargeIcon height={16} tooltip="Close" />
          </IconButton>
        </DialogTitle>
        <div className="immediate-coaching-content">
          <section className="body-wrapper">
            {showLiveCoaching && (
              <WavyCard color={theme.palette.christine} title={'Live Coaching'}>
                {loading ? (
                  <Loading />
                ) : (
                  <p>
                    Provide feedback on a call as it's happening. We'll ask you to provide call
                    details.
                  </p>
                )}
                <Button
                  variant="contained"
                  color="primary"
                  fullWidth
                  className={classNames(['create-button', 'live', loading && 'disabled'])}
                  onClick={!loading && this.startLiveCoaching}>
                  Live Coach
                </Button>
              </WavyCard>
            )}
            {!launchedWithCall && hasNoCallReviewType && (
              <WavyCard
                color={getMuiV4Theme().palette.primary.main}
                title={'1 on 1 Meeting'}
                className={classNames(
                  'standard-meeting-card',
                  settingUpAdhocMeeting ? 'begin' : 'choose'
                )}>
                {!settingUpAdhocMeeting && !noCallNoRep ? adhocSelection : setUpAdhoc}
              </WavyCard>
            )}
            {launchedWithCall && (
              <WavyCard
                color={getMuiV4Theme().palette.primary.main}
                title={'Coach This Call'}
                className={classNames('standard-meeting-card', 'choose')}>
                {setUpAdhoc}
              </WavyCard>
            )}
          </section>

          {!launchedWithCall && !settingUpAdhocMeeting && !noCallNoRep && (
            <section className="ev-helper">
              <CallToAction
                text={
                  <div>
                    Want to coach an existing call?{' '}
                    <Link
                      to={{
                        pathname: '/calls/my',
                        state: {rep},
                      }}
                      className={'generic-link'}>
                      Click here
                    </Link>{' '}
                    to go to the Calls Tab and click "Coach This Call"
                  </div>
                }
              />
            </section>
          )}
          {launchedWithCall && (
            <section className="ev-helper">
              <CallToAction
                text={
                  <div>
                    <Link onClick={this.startLiveCoaching} className={'generic-link'}>
                      Click here
                    </Link>{' '}
                    to begin coaching a live call {rep ? `with ${rep.firstName}` : ''} instead.
                  </div>
                }
              />
            </section>
          )}
        </div>
      </Dialog>
    );
  }
}
